import { Document, Page, View, Text } from '@react-pdf/renderer';
import type { WindowConfig, CurtainStyleConfig, FabricConfig, LiningConfig, HardwareConfig, CutCalculation } from '@/types/curtain';
import { baseStyles, BRAND, fmtM, fmtMm, fmtCurrency, generateOrderNumber } from './pdfStyles';

interface PurchaseOrderPDFProps {
  customerName: string;
  windowConfig: Partial<WindowConfig>;
  styleConfig: Partial<CurtainStyleConfig>;
  fabricConfig: Partial<FabricConfig>;
  liningConfig: Partial<LiningConfig>;
  hardwareConfig: Partial<HardwareConfig>;
  calculation: CutCalculation;
  fabricCost: number;
  liningCost: number;
  hardwareCost: number;
  totalCost: number;
}

export function PurchaseOrderPDF(props: PurchaseOrderPDFProps) {
  const {
    customerName, windowConfig, styleConfig, fabricConfig,
    liningConfig, hardwareConfig, calculation,
    fabricCost, liningCost, hardwareCost, totalCost,
  } = props;

  const orderNumber = generateOrderNumber();
  const today = new Date();
  const windowRef = `${windowConfig.roomName || 'Room'} — ${windowConfig.windowName || 'Window'}`;

  return (
    <Document title={`Purchase Order - ${orderNumber}`} author="Hail Mary Curtains">
      <Page size="A4" style={baseStyles.page}>
        {/* Header */}
        <View style={baseStyles.headerRow}>
          <View>
            <Text style={baseStyles.brandName}>Hail Mary Curtains</Text>
            <Text style={{ fontSize: 8, color: BRAND.slate500, marginTop: 2 }}>Supplier Purchase Order</Text>
          </View>
          <View>
            <Text style={baseStyles.docTitle}>PURCHASE ORDER</Text>
            <Text style={baseStyles.metaText}>PO# {orderNumber}</Text>
            <Text style={baseStyles.metaText}>Date: {today.toLocaleDateString()}</Text>
          </View>
        </View>

        {/* Order Reference */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 16 }}>
          <View style={{ width: '48%' }}>
            <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: BRAND.navy, marginBottom: 4 }}>ORDER FOR:</Text>
            <Text style={{ fontSize: 9, color: BRAND.slate700 }}>{customerName}</Text>
            <Text style={{ fontSize: 9, color: BRAND.slate500 }}>{windowRef}</Text>
          </View>
          <View style={{ width: '48%' }}>
            <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: BRAND.navy, marginBottom: 4 }}>SUPPLIER:</Text>
            <Text style={{ fontSize: 9, color: BRAND.slate500 }}>[Supplier name to be filled]</Text>
          </View>
        </View>

        {/* Fabric Order */}
        <Text style={baseStyles.sectionTitle}>Fabric Order</Text>
        <View style={baseStyles.tableHeader}>
          <Text style={[baseStyles.tableHeaderCell, { width: '20%' }]}>Code</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '15%' }]}>Type</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '15%' }]}>Roll Width</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '12%' }]}>V.Repeat</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '13%' }]}>Qty (m)</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '12%' }]}>Unit $/m</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '13%', textAlign: 'right' }]}>Line Total</Text>
        </View>
        <View style={baseStyles.tableRow}>
          <Text style={[baseStyles.tableCell, { width: '20%', fontFamily: 'Helvetica-Bold' }]}>{fabricConfig.fabricCode || 'CUSTOM'}</Text>
          <Text style={[baseStyles.tableCell, { width: '15%' }]}>{(fabricConfig.fabricType || 'linen').toUpperCase()}</Text>
          <Text style={[baseStyles.tableCell, { width: '15%' }]}>{fmtMm(fabricConfig.fabricWidth || 1400)}</Text>
          <Text style={[baseStyles.tableCell, { width: '12%' }]}>{fabricConfig.verticalRepeat ? fmtMm(fabricConfig.verticalRepeat) : 'Plain'}</Text>
          <Text style={[baseStyles.tableCell, { width: '13%' }]}>{fmtM(calculation.totalFabricMeters)}</Text>
          <Text style={[baseStyles.tableCell, { width: '12%' }]}>{fmtCurrency(fabricConfig.pricePerMeter || 0)}</Text>
          <Text style={[baseStyles.tableCell, { width: '13%', textAlign: 'right', fontFamily: 'Helvetica-Bold' }]}>{fmtCurrency(fabricCost)}</Text>
        </View>

        {/* Lining Order */}
        <Text style={baseStyles.sectionTitle}>Lining Order</Text>
        <View style={baseStyles.tableHeader}>
          <Text style={[baseStyles.tableHeaderCell, { width: '30%' }]}>Lining Type</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '20%' }]}>Width</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '15%' }]}>Qty (m)</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '15%' }]}>Unit $/m</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '20%', textAlign: 'right' }]}>Line Total</Text>
        </View>
        <View style={baseStyles.tableRow}>
          <Text style={[baseStyles.tableCell, { width: '30%', fontFamily: 'Helvetica-Bold' }]}>{(liningConfig.liningType || 'standard').toUpperCase()}</Text>
          <Text style={[baseStyles.tableCell, { width: '20%' }]}>{fmtMm(liningConfig.liningWidth || 1400)}</Text>
          <Text style={[baseStyles.tableCell, { width: '15%' }]}>{fmtM(calculation.liningMeters)}</Text>
          <Text style={[baseStyles.tableCell, { width: '15%' }]}>{fmtCurrency(liningConfig.pricePerMeter || 0)}</Text>
          <Text style={[baseStyles.tableCell, { width: '20%', textAlign: 'right', fontFamily: 'Helvetica-Bold' }]}>{fmtCurrency(liningCost)}</Text>
        </View>

        {/* Hardware */}
        <Text style={baseStyles.sectionTitle}>Hardware & Accessories</Text>
        <View style={baseStyles.tableHeader}>
          <Text style={[baseStyles.tableHeaderCell, { width: '30%' }]}>Item</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '25%' }]}>Style</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '20%' }]}>Mount</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '25%', textAlign: 'right' }]}>Cost</Text>
        </View>
        <View style={baseStyles.tableRow}>
          <Text style={[baseStyles.tableCell, { width: '30%' }]}>Track / Rod System</Text>
          <Text style={[baseStyles.tableCell, { width: '25%' }]}>{(hardwareConfig.style || 'architectural').toUpperCase()}</Text>
          <Text style={[baseStyles.tableCell, { width: '20%' }]}>{(windowConfig.mountType || 'wall').toUpperCase()}</Text>
          <Text style={[baseStyles.tableCell, { width: '25%', textAlign: 'right', fontFamily: 'Helvetica-Bold' }]}>{fmtCurrency(hardwareCost)}</Text>
        </View>

        {/* Specification Summary */}
        <Text style={baseStyles.sectionTitle}>Specification Summary</Text>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Heading Style:</Text>
          <Text style={baseStyles.kvValue}>{(styleConfig.headingStyle || 's-fold').toUpperCase()}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Panel Layout:</Text>
          <Text style={baseStyles.kvValue}>{styleConfig.isPair ? 'PAIR' : 'SINGLE'}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Track Width:</Text>
          <Text style={baseStyles.kvValue}>{fmtMm(windowConfig.trackWidth || 0)}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Number of Fabric Widths:</Text>
          <Text style={baseStyles.kvValue}>{calculation.numberOfWidths}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Wastage Estimate:</Text>
          <Text style={baseStyles.kvValue}>{calculation.wastagePercent.toFixed(1)}%</Text>
        </View>

        {/* Grand Total */}
        <View style={[baseStyles.totalRow, { marginTop: 20 }]}>
          <Text style={baseStyles.totalLabel}>PURCHASE ORDER TOTAL</Text>
          <Text style={baseStyles.totalValue}>{fmtCurrency(totalCost)}</Text>
        </View>

        {/* Notes */}
        <View style={baseStyles.noteBox}>
          <Text style={baseStyles.noteText}>
            Please confirm stock availability and lead time upon receipt of this PO.
            All fabric must be from the same dye lot. Verify roll width and pattern repeat before cutting.
          </Text>
        </View>

        {/* Footer */}
        <View style={baseStyles.footer} fixed>
          <Text>Hail Mary Curtains — Supplier Purchase Order</Text>
          <Text>PO# {orderNumber}</Text>
        </View>
      </Page>
    </Document>
  );
}
